import { useEffect, useMemo, useRef } from 'react';

import { usePathname, useRouter } from '@flipster/i18n/routing';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { css } from '@flipster/styled-system/css';

import { useCheckSpotOnly, useIsRegulationLoading } from '../../../../shared/regulation';
import { useIsWebview } from '../../../../shared/webview';
import { HistoryTabGroup, getTabList } from '../../constants';

const Tabs = createTabsComponent<string>();

interface Props {
  historyTabGroup: HistoryTabGroup;
}

const MobileHistoryTab = ({ historyTabGroup }: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const isSpotOnly = useCheckSpotOnly();
  const isRegulationLoading = useIsRegulationLoading();
  const isWebview = useIsWebview();
  // 규제 정보가 오기 전에는 어떤 탭을 숨겨야 할지 알 수 없다(isSpotOnly가 기본값 false로 떨어진다).
  // 목록을 비워두면 아래 스크롤·프리페치 이펙트도 자연히 아무 일도 하지 않는다.
  const tabList = useMemo(
    () => (isRegulationLoading ? [] : getTabList(historyTabGroup, { isSpotOnly, isWebview })),
    [isRegulationLoading, historyTabGroup, isSpotOnly, isWebview],
  );
  const tabsRef = useRef<HTMLDivElement | null>(null);

  // 모바일에서 특정 히스토리 페이지로 바로 진입하면, 선택된 탭이 스크롤 밖에 있을 수 있다.
  // 디자인 시스템 Tabs는 클릭 시에만 스크롤을 맞춰주기 때문에 최초 진입분은 여기서 처리한다.
  //
  // tabList도 의존성에 넣는다. 목록은 렌더 뒤에도 바뀐다 — 규제 정보가 도착해 비어 있던 목록이
  // 처음 채워질 때, 그리고 웹 도메인을 앱 웹뷰로 띄워 isWebview가 뒤집히며 줄어들 때.
  // pathname만 보면 그때 다시 맞추지 못해 선택 탭이 어긋난 자리에 남는다.
  useEffect(() => {
    tabsRef.current
      ?.querySelector('button[aria-selected=true]')
      ?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [pathname, tabList]);

  // 탭 이동이 <button>이라 Next의 <Link> 자동 프리페치가 걸리지 않는다.
  // 누른 뒤에야 RSC 페이로드와 청크를 받기 시작해 전환이 느리므로 형제 탭을 미리 받아둔다.
  useEffect(() => {
    tabList.forEach(({ value }) => {
      if (value !== pathname) router.prefetch(value);
    });
  }, [router, tabList, pathname]);

  // 목록이 비어 있는 동안 높이를 잡아둬야 응답 후 레이아웃이 밀리지 않는다.
  // 44 = 디자인 시스템 Tabs의 medium 높이
  if (isRegulationLoading) return <div className={css({ h: 44 })} />;

  return (
    <div ref={tabsRef}>
      <Tabs
        type='scrollable'
        value={pathname}
        // clicking selected tab again should clean all sub-filters.
        // using onChange prop blocks this feature.
        onClick={(tab) => router.replace(tab)}
        tabsCss={{ px: 16 }}
      >
        {tabList.map((tab) => (
          <Tabs.Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>
    </div>
  );
};

export default MobileHistoryTab;
